/**
 * The Credit block on a company record — owner decision 2026-09-24.
 *
 * Two reads, fired together:
 *
 *   - `GET /credit-notes/balances?companyId=N` — the remaining balance, ONE ROW
 *     PER CURRENCY. Rendered through `balancePills` / `balanceTitle` and never
 *     summed, converted or sorted here.
 *   - `GET /credit-notes?companyId=N` — a page of the company's credit notes,
 *     each linking to `/credit-notes/:id`.
 *
 * ⛔ **NOTHING HERE ADDS MONEY UP.** The balance comes from the server, which
 * computes it as spendable credit minus allocations. The list is shown beside
 * it and is never totalled: it is one page of N, it carries drafts and voided
 * notes, and it does not carry the allocations.
 *
 * ## The states, and why each is kept apart
 *
 * | state | renders |
 * | --- | --- |
 * | loading | skeletons in the real shape |
 * | `[]` balances, no notes at all | **No credit** |
 * | `[]` balances, notes exist | **No spendable credit**, and the notes listed with their status |
 * | a failed read | **Couldn't load …** — never "No credit" |
 * | `403` | **Your account is not allowed to read credit notes.** |
 *
 * ⚠️ **A failed read and an empty one are different facts.** `null` in state
 * means *not read*; `[]` means *read, and there is none*. There is no `?? []`
 * anywhere between the wire and the screen.
 *
 * ⚠️ **The refusal is split on the STATUS (`403`), never on the message.** The
 * server's own sentence is rendered beside ours: ours says which KIND of answer
 * this is, theirs says why — the same shape as `CreditNotesPage`.
 *
 * ⚠️ **The block is laid out to match the company page's `DetailGroup`** (its
 * padding, hairline and heading) rather than rendered inside one: that group
 * wraps its children in a `<dl>` grid, and a list of documents does not belong
 * in a description list.
 *
 * ## 2026-10-11: the balance block is EXPORTED for the history section
 *
 * The company page's "Sales & billing history" section renders the per-currency
 * balance above its Credit notes tab, so the balance block, the read union and
 * the refusal sentence are exported — `CompanyCreditBalance`, `CreditRead`,
 * `creditReadFailureOf`, `CreditRefusal` — and consumed by
 * `CompanyDocumentsHistory`. **Every state and every sentence above is kept**;
 * what moved is only the shell around them. The standalone panel below still
 * compiles and still renders its own notes list for any caller that mounts it,
 * but the history section REPLACES that list with its own table, so a page must
 * mount one or the other and never both.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Badge, Skeleton, ValuePills } from '@/components/ui';
import { Money } from '@/components/ui/Money';
import { TextLink } from '@/components/ui/TextLink';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import {
  CREDIT_BALANCE_LABEL,
  CREDIT_NOTES_LABEL,
  CREDIT_REMAINING_LABEL,
  balancePills,
  balanceTitle,
  creditNoteRef,
  creditNoteStatusTone,
  spendableOf,
} from '@/lib/credit-notes';
import type { CompanyCreditBalance, CreditNote } from '@/lib/types';

/** How many notes the block lists before sending the reader to the full list. */
const NOTES_PER_PAGE = 10;

/**
 * One read's state. `null`-free on purpose: *not read* is `loading`, *read and
 * empty* is `ok` with `[]`, and the three failures are told apart so none of
 * them can render as the others.
 */
export type CreditRead<T> =
  | { kind: 'loading' }
  | { kind: 'ok'; data: T }
  | { kind: 'forbidden'; message: string }
  // 2026-10-09: the reader may read credit notes but not THIS company's
  // balance (own scope, not its account lead). Not a module refusal and not a
  // failure, so it gets neither the top-level refusal nor "Couldn't load".
  | { kind: 'notOwned'; message: string }
  | { kind: 'error'; message: string };

type Read<T> = CreditRead<T>;

interface NotesPage {
  notes: readonly CreditNote[];
  total: number;
}

/**
 * Classifies a failed credit read. ⚠️ **The refusal is split on the STATUS,
 * never on the message**, and `CREDIT_BALANCE_NOT_OWNED` is checked first
 * because it also arrives as a 403.
 */
export function creditReadFailureOf<T>(caught: unknown): CreditRead<T> {
  if (caught instanceof ApiError && caught.code === 'CREDIT_BALANCE_NOT_OWNED') {
    return { kind: 'notOwned', message: caught.message };
  }
  if (caught instanceof ApiError && caught.status === 403) {
    return { kind: 'forbidden', message: caught.message };
  }
  return {
    kind: 'error',
    message: caught instanceof Error ? caught.message : 'The request failed.',
  };
}

/** ⚠️ Mount with `key={companyId}` — the component does not reset on a prop change. */
export function CompanyCreditPanel({ companyId }: { companyId: number }) {
  const [balances, setBalances] = useState<Read<readonly CompanyCreditBalance[]>>({
    kind: 'loading',
  });
  const [notes, setNotes] = useState<Read<NotesPage>>({ kind: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    // No reset to `loading` here: the caller keys this component by
    // `companyId`, so a different company is a fresh mount with fresh state
    // rather than the old company's figures lingering under the new name.

    api
      .getCompanyCreditBalances(companyId, signal)
      .then((response) => {
        if (signal.aborted) return;
        setBalances({ kind: 'ok', data: response.data });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setBalances(creditReadFailureOf(caught));
      });

    api
      .listCreditNotes({ companyId, perPage: NOTES_PER_PAGE }, signal)
      .then((response) => {
        if (signal.aborted) return;
        setNotes({ kind: 'ok', data: { notes: response.data, total: response.meta.total } });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setNotes(creditReadFailureOf(caught));
      });

    return () => controller.abort();
  }, [companyId]);

  /*
   * Both reads are gated on the same `credit-notes.read` grant, so a refusal on
   * either is a refusal of the whole block — said once, with no figure and no
   * list, rather than as one half refused and the other failed.
   */
  const forbidden =
    balances.kind === 'forbidden' ? balances : notes.kind === 'forbidden' ? notes : null;

  return (
    <div className="border-b border-border px-4 py-4 last:border-b-0">
      <h3 className="m-0 text-sm font-semibold text-fg">Credit</h3>
      {forbidden ? (
        <CreditRefusal message={forbidden.message} className="mt-3" />
      ) : (
        <div className="mt-3 flex flex-col gap-4">
          <CompanyCreditBalance
            balances={balances}
            notesTotal={notes.kind === 'ok' ? notes.data.total : null}
          />
          <NotesSection notes={notes} companyId={companyId} />
        </div>
      )}
    </div>
  );
}

/**
 * The one refusal sentence for the credit-notes grant, ours first and the
 * server's beside it. `role="status"`, not `alert`: a refusal is an ordinary
 * answer about the reader, not a failure of the page.
 */
export function CreditRefusal({ message, className }: { message: string; className?: string }) {
  return (
    <p
      role="status"
      className={cx('m-0 flex flex-col gap-0.5 text-sm text-fg-secondary', className)}
    >
      <span className="font-semibold text-fg">Your account is not allowed to read credit notes.</span>
      <span className="min-w-0 text-xs text-fg-muted [overflow-wrap:anywhere]">{message}</span>
    </p>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <p className="m-0 text-xs font-medium text-fg-muted">
      {children}
    </p>
  );
}

function Failure({ what, message }: { what: string; message: string }) {
  return (
    <p role="alert" className="m-0 mt-1 flex flex-col gap-0.5 text-sm text-danger">
      <span className="font-semibold">Couldn’t load {what}.</span>
      <span className="min-w-0 text-xs [overflow-wrap:anywhere]">{message}</span>
    </p>
  );
}

/**
 * The balance, one currency per entry.
 *
 * 🔴 **`balanceTitle` IS LOAD-BEARING.** The label is a comma-joined run of one
 * figure per currency and can wrap or be cut; the title is where the whole
 * balance stays readable. Rendering the label without it builds a cell that
 * silently omits money.
 *
 * ⚠️ **`[]` reads "No credit" only when the NOTES read also came back empty.**
 * A company holding drafts or voided notes has no spendable credit AND has
 * credit notes, and "No credit" over a list of three notes reads as the list
 * being wrong. So `[]` beside notes reads **No spendable credit** — true
 * whatever the notes are, because `[]` is the server saying nothing is
 * spendable. While the notes are loading or failed (`notesTotal === null`),
 * the same wording is used: it is the claim the balance read supports on its
 * own.
 *
 * Exported with no shell of its own so the history section can set it above
 * its Credit notes table; the panel above mounts it under its own heading.
 */
export function CompanyCreditBalance({
  balances,
  notesTotal,
}: {
  balances: CreditRead<readonly CompanyCreditBalance[]>;
  /** `meta.total` of the company's credit notes, or `null` while unknown. */
  notesTotal: number | null;
}) {
  let body: ReactNode;
  switch (balances.kind) {
    case 'loading':
      body = <Skeleton className="mt-1.5 h-4 w-40" />;
      break;
    case 'notOwned':
      body = <p className="m-0 mt-1 text-sm text-fg-secondary">{balances.message}</p>;
      break;
    case 'forbidden':
    case 'error':
      body = <Failure what="the credit balance" message={balances.message} />;
      break;
    case 'ok':
      if (balances.data.length === 0) {
        const noNotes = notesTotal === 0;
        body = (
          <p className="m-0 mt-1 text-sm text-fg-secondary">
            {noNotes ? 'No credit' : 'No spendable credit'}
          </p>
        );
      } else {
        body = (
          <ValuePills
            className="mt-1 tabular"
            title={balanceTitle(balances.data)}
            items={balancePills(balances.data)}
          />
        );
      }
      break;
  }
  return (
    <div className="min-w-0">
      <SectionLabel>{CREDIT_BALANCE_LABEL}</SectionLabel>
      {body}
    </div>
  );
}

/**
 * A page of the company's credit notes.
 *
 * ⚠️ **Remaining is shown beside a `Not spendable` badge where the server says
 * so.** `remainingAmount` is reported even on a DRAFT, where it is the note's
 * whole value and can be put against nothing; the figure alone would make a
 * draft read as available credit.
 *
 * ⚠️ **Rendered in the order the server sent.** Nothing here re-sorts.
 *
 * The link past the first page goes to `/credit-notes?companyId=N`, which that
 * page reads from its query string and shows as a filter badge.
 */
function NotesSection({ notes, companyId }: { notes: Read<NotesPage>; companyId: number }) {
  let body: ReactNode;
  switch (notes.kind) {
    case 'loading':
      body = (
        <div className="mt-1.5 flex flex-col gap-2">
          <Skeleton className="h-4 w-full max-w-md" />
          <Skeleton className="h-4 w-full max-w-sm" />
        </div>
      );
      break;
    case 'forbidden':
    case 'error':
      body = <Failure what="credit notes" message={notes.message} />;
      break;
    case 'ok': {
      const { notes: rows, total } = notes.data;
      if (rows.length === 0) {
        body = <p className="m-0 mt-1 text-sm text-fg-secondary">No credit notes</p>;
        break;
      }
      body = (
        <>
          <ul className="m-0 mt-1.5 flex list-none flex-col divide-y divide-border p-0">
            {rows.map((note) => (
              <NoteRow key={note.id} note={note} />
            ))}
          </ul>
          {total > rows.length && (
            <TextLink
              to={`/credit-notes?companyId=${companyId}`}
              className="mt-2 inline-block text-sm font-medium"
            >
              All {total} credit notes
            </TextLink>
          )}
        </>
      );
      break;
    }
  }
  return (
    <div className="min-w-0">
      <SectionLabel>{CREDIT_NOTES_LABEL}</SectionLabel>
      {body}
    </div>
  );
}

function NoteRow({ note }: { note: CreditNote }) {
  const ref = creditNoteRef(note);
  return (
    <li className="flex flex-col gap-1 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <TextLink
          to={`/credit-notes/${note.id}`}
          className="min-w-0 truncate text-sm font-medium"
          title={ref}
        >
          {ref}
        </TextLink>
        <Badge tone={creditNoteStatusTone(note.status)}>{note.status.name}</Badge>
      </div>
      <dl className="m-0 flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1 text-sm tabular">
        <div className="flex items-center gap-1.5">
          <dt className="text-xs text-fg-muted">Total</dt>
          <dd className="m-0 text-fg-secondary">
            <Money amount={note.grandTotal} currency={note.currency} />
          </dd>
        </div>
        <div className="flex items-center gap-1.5">
          <dt className="text-xs text-fg-muted">{CREDIT_REMAINING_LABEL}</dt>
          <dd className="m-0 inline-flex flex-wrap items-center gap-1.5 text-fg-secondary">
            <Money amount={note.remainingAmount} currency={note.currency} />
            {!spendableOf(note) && (
              <Badge tone="neutral" variant="outline">
                Not spendable
              </Badge>
            )}
          </dd>
        </div>
      </dl>
    </li>
  );
}
